import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";

interface DbpjkitWorkerJobRerunDialogPresenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validating: boolean;
  rerunnable: boolean;
  dryRunSupported: boolean;
  reasons: string[];
  rerunning: boolean;
  onRerun: (dryRun: boolean) => void;
}

export function DbpjkitWorkerJobRerunDialogPresenter({
  open,
  onOpenChange,
  validating,
  rerunnable,
  dryRunSupported,
  reasons,
  rerunning,
  onRerun,
}: DbpjkitWorkerJobRerunDialogPresenterProps) {
  const { t } = useTranslation();
  const actionsDisabled = validating || rerunning || !rerunnable;

  return (
    // The dialog can be dismissed at any time, even mid-rerun: the action buttons'
    // disabled state already guards against duplicate submissions, and the outcome
    // reaches the user via the snackbar either way.
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("ui.rerunX", { x: t("ui.thisJob") })}</DialogTitle>
          <DialogDescription>{t("ui.rerunDescription")}</DialogDescription>
        </DialogHeader>
        {validating ? (
          <p className="text-14-regular text-fg-2">{t("ui.rerunValidating")}</p>
        ) : !rerunnable ? (
          <div className="space-y-1.5">
            <p className="text-14-regular text-fg-1">{t("ui.rerunNotAllowed")}</p>
            {reasons.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-[13px] text-fg-2">
                {reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        ) : !dryRunSupported ? (
          <p className="flex items-center gap-2 rounded-[var(--radius-sm)] bg-warning-bg px-3 py-2 text-12-regular text-fg-1">
            <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
            {t("ui.dryRunUnsupportedWarning")}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={actionsDisabled || !dryRunSupported}
            onClick={() => onRerun(true)}
          >
            {t("ui.rerunAsDryRun")}
          </Button>
          <Button type="button" disabled={actionsDisabled} onClick={() => onRerun(false)}>
            {t("ui.rerunLive")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
